import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, ShieldCheck, Send, Clock, Users, MessageCircle, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { TeamPerson } from "@/components/team/TeamPerson";
import { ModApplyDialog } from "@/components/team/ModApplyDialog";

const ROLES = [
  { icon: Crown, title: "Projektleitung", cls: "from-amber-300 to-amber-500 text-[#4a2a08]", text: "Gründet, führt und entwickelt die itzPlay Community. Die Projektleitung entscheidet über neue Funktionen, Prämien, Regeln und Events und ist für alles verantwortlich." },
  { icon: ShieldCheck, title: "Moderator", cls: "from-sky-400 to-blue-500 text-white", text: "Achtet ehrenamtlich darauf, dass sich alle wohlfühlen: prüft Meldungen, hilft bei Fragen, verwarnt bei Regelverstößen und hält Feed und Chats sauber. Erkennbar am blauen Haken." },
];

function ContactBox({ data }) {
  const others = (l) => l.filter((p) => p.user_id !== data.me);
  const pool = others(data.moderators).length ? others(data.moderators) : others(data.leads);
  const [pick] = useState(() => pool[Math.floor(Math.random() * pool.length)]);
  if (!pick) return null;
  return (
    <section className="lux-card rounded-3xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="team-contact-box">
      <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-400 to-blue-500 text-white flex items-center justify-center shrink-0"><MessageCircle className="w-6 h-6" /></span>
      <div className="flex-1 min-w-0">
        <p className="font-display font-extrabold text-slate-800">Fragen, Probleme oder Ideen?</p>
        <p className="text-sm text-slate-500">Schreib direkt einem Moderator – wir helfen dir gern weiter.</p>
      </div>
      <Link to={`/nachrichten/${pick.user_id}`} className="shrink-0 inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full pink-gradient text-white font-bold text-sm" data-testid="team-contact-btn"><MessageCircle className="w-4 h-4" /> Moderator schreiben</Link>
    </section>
  );
}

const Section = ({ icon: Icon, title, children, testId }) => (
  <section className="space-y-3" data-testid={testId}>
    <h2 className="flex items-center gap-2 font-display text-lg font-extrabold text-slate-800"><Icon className="w-5 h-5 text-rose-500" /> {title}</h2>
    {children}
  </section>
);

function ApplyBox({ data, onApplied }) {
  const [open, setOpen] = useState(false);
  if (data.is_staff) return null;
  const pending = data.my_application?.status === "pending";
  return (
    <section className="rounded-[2rem] pink-gradient text-white p-6 text-center space-y-3 shadow-xl shadow-rose-200" data-testid="team-apply-box">
      <ShieldCheck className="w-10 h-10 mx-auto" />
      <h2 className="font-display text-2xl font-extrabold">Werde Teil des Teams</h2>
      <p className="text-sm text-white/90 max-w-md mx-auto">Du bist oft hier, hilfsbereit und hast ein Herz für die Community? Dann bewirb dich als Moderator – wir freuen uns auf dich!</p>
      {pending
        ? <p className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white/20 font-bold" data-testid="team-apply-pending"><Clock className="w-4 h-4" /> Deine Bewerbung wird geprüft</p>
        : <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-white text-rose-600 font-black shadow-lg hover:scale-[1.03] transition-transform" data-testid="team-apply-btn"><Send className="w-4 h-4" /> Als Moderator bewerben</button>}
      <ModApplyDialog open={open} onOpenChange={setOpen} onDone={onApplied} />
    </section>
  );
}

export default function Team() {
  const [data, setData] = useState(null);
  const load = () => api.get("/staff").then(({ data }) => setData(data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);
  return (
    <div className="max-w-2xl mx-auto space-y-8 pb-10" data-testid="team-page">
      <header className="space-y-2">
        <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-rose-500">itzPlay</p>
        <h1 className="text-4xl sm:text-5xl font-display font-extrabold tracking-tight text-slate-800">Unser Team</h1>
        <p className="text-slate-500">Diese Menschen stecken hinter der Community und kümmern sich darum, dass sich hier alle wohlfühlen.</p>
      </header>
      {data === null && <p className="text-slate-400 text-sm">Lädt …</p>}
      {data && <>
        <ContactBox data={data} />
        <Link to="/danke-stefan" data-testid="team-thanks-stefan" className="flex items-center gap-4 p-4 rounded-3xl bg-gradient-to-br from-rose-50 via-white to-amber-50 ring-1 ring-amber-300 active:scale-[0.98] transition-transform">
          <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-300 to-rose-400 grid place-items-center shrink-0"><Crown className="w-6 h-6 text-white" /></span>
          <span className="flex-1 min-w-0"><span className="block font-extrabold text-slate-900">Danke, Stefan Lageder!</span><span className="block text-xs text-slate-500">Moderator & Top Projekt Unterstützer – von Anfang an dabei</span></span>
          <ChevronRight className="w-5 h-5 text-rose-500 shrink-0" />
        </Link>
        <Section icon={Crown} title="Projektleitung" testId="team-leads">
          {data.leads.map((p, i) => <TeamPerson key={p.user_id} p={p} lead i={i} me={data.me} />)}
        </Section>
        <Section icon={ShieldCheck} title={`Moderatoren (${data.moderators.length})`} testId="team-mods">
          {data.moderators.length ? data.moderators.map((p, i) => <TeamPerson key={p.user_id} p={p} i={i} me={data.me} />)
            : <p className="text-sm text-slate-500 lux-card rounded-3xl p-4" data-testid="team-mods-empty">Noch keine Moderatoren – vielleicht bist du die erste Person?</p>}
        </Section>
        <Section icon={Users} title="Was bedeuten die Rollen?" testId="team-roles">
          <div className="grid sm:grid-cols-2 gap-3">
            {ROLES.map((r) => (
              <div key={r.title} className="lux-card rounded-3xl p-4 space-y-2" data-testid={`team-role-${r.title}`}>
                <span className={`inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r px-3 py-1 text-xs font-black ${r.cls}`}><r.icon className="w-3.5 h-3.5" /> {r.title}</span>
                <p className="text-sm text-slate-600 leading-relaxed">{r.text}</p>
              </div>
            ))}
          </div>
        </Section>
        <ApplyBox data={data} onApplied={load} />
      </>}
    </div>
  );
}
